import { SearchQConfigsDocument, useCreateQConfigMutation } from '@exam-management/graphql';
import { useParams, useNavigate } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Flex,
  Form,
  FormNumberInput,
  FormTextField,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

type CreateQConfigFormInput = z.infer<typeof CreateQConfigFormSchema>;

const NAME_MAX_LENGTH = 1024;
const NAME_MIN_LENGTH = 1;

const TOTAL_QUESTIONS_MIN = 0;

const CreateQConfigFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(NAME_MIN_LENGTH, {
      message: 'กรุณากรอกชื่อ QConfig',
    })
    .max(NAME_MAX_LENGTH, {
      message: `ความยาวต้องไม่เกิน ${NAME_MAX_LENGTH} ตัวอักษร`,
    }),
  totalQuestion: z
    .unknown()
    .transform((val) => {
      if (typeof val === 'string') return parseInt(val);
      else if (typeof val === 'number') return val;
      else return undefined;
    })
    .pipe(
      z
        .number({
          message: 'กรุณาระบุจำนวนข้อสอบ',
        })
        .min(TOTAL_QUESTIONS_MIN, {
          message: `กรุณาระบุอย่างน้อย ${TOTAL_QUESTIONS_MIN} ข้อ`,
        })
        .int({
          message: 'กรุณาระบุจำนวนข้อสอบเป็นจำนวนเต็ม',
        }),
    ),
});

export interface CreateQConfigFormProps {
  onClose: () => void;
}

export function CreateQConfigForm(props: Readonly<CreateQConfigFormProps>) {
  const { onClose } = props;
  const { courseId } = useParams('/exam-courses/:courseId/qconfigs');
  const navigate = useNavigate();

  const formContext = useForm<CreateQConfigFormInput>({
    defaultValues: {
      name: '',
    },
    resolver: zodResolver(CreateQConfigFormSchema),
  });

  const { showSnackbar } = useSnackbar();

  const [createQConfig, { loading: isLoading }] = useCreateQConfigMutation({
    onCompleted: (res) => {
      showSnackbar({
        message: 'QConfig created successfully',
        variant: 'success',
      });

      navigate(`/exam-courses/:courseId/qconfigs/:qconfigId`, {
        params: {
          courseId: courseId,
          qconfigId: res.excCreateQConfig.qConfig.id,
        },
        replace: true,
      });
    },
    onError: () => {
      showSnackbar({
        message: 'QConfig created failed',
        variant: 'error',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [SearchQConfigsDocument],
  });

  const handleSubmit = async (data: CreateQConfigFormInput) => {
    await createQConfig({
      variables: {
        createQConfigInput: {
          examCourseId: courseId,
          name: data.name,
          totalQuestions: data.totalQuestion,
        },
      },
    });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleSubmit)}>
        <DialogContent>
          <Flex
            sx={{
              flexDirection: 'column',
              gap: (theme) => theme.spacing(4),
            }}
          >
            <FormTextField
              label='ชื่อ QConfig'
              name='name'
              placeholder='กรอกชื่อ QConfig'
              fullWidth
              required
            />
            <FormNumberInput
              label='จำนวนข้อสอบ'
              name='totalQuestion'
              placeholder='ระบุจำนวนข้อสอบ'
              sx={{
                maxWidth: 180,
              }}
              required
            />
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            onClick={handleClose}
            size='large'
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type='submit' size='large' loading={isLoading}>
            Create QConfig
          </Button>
        </DialogActions>
      </Form>
    </FormProvider>
  );
}
